// O mapa de telas do painel: a única lista do menu. A ordem e os rótulos são
// os do menu do painel antigo, pra ninguém se perder. Desde a 3.12 toda tela é
// React; o endereço antigo (/dashboard?tab=x) é traduzido no servidor
// (api/src/modules/entrada/painel-antigo.ts).
import type { Papel } from '../../shared/api/tipos';

export interface Tela {
  chave: string;
  rotulo: string;
  icone: string;
  /** A rota em React. */
  rota: string;
  grupo: 'atendimento' | 'bia' | 'loja' | 'vendas' | 'estudio' | 'conta' | 'plataforma';
  /** Quem vê: por padrão todo mundo da loja. */
  papeis?: Papel[];
  /**
   * Só quem é da plataforma vê, em qualquer loja e com qualquer papel. Não é
   * um papel a mais na lista porque não depende da loja aberta: o Bruno na
   * própria loja é `dono` e precisa achar a lista de lojas do mesmo jeito.
   */
  soSuperAdmin?: boolean;
  /**
   * Só aparece para a loja que o servidor liberou (Anúncios, liberado aos
   * poucos pela lista ANUNCIOS_LOJAS): o estado do módulo diz `liberado`.
   */
  liberacao?: 'anuncios';
}

const ADMIN: Papel[] = ['dono', 'admin'];

export const TELAS: Tela[] = [
  { chave: 'inicio', rotulo: 'Início', icone: '🏠', rota: '/app', grupo: 'atendimento' },
  { chave: 'conversas', rotulo: 'Mensagens', icone: '💬', rota: '/app/conversas', grupo: 'atendimento' },
  { chave: 'agentes', rotulo: 'Personalidade', icone: '👤', rota: '/app/agentes', grupo: 'bia' },
  { chave: 'conhecimento', rotulo: 'Base de Conhecimento', icone: '📚', rota: '/app/conhecimento', grupo: 'bia' },
  { chave: 'entrega', rotulo: 'Entrega', icone: '🚚', rota: '/app/entrega', grupo: 'bia', papeis: ADMIN },
  { chave: 'pagamentos', rotulo: 'Formas de Pagamento', icone: '💳', rota: '/app/pagamentos', grupo: 'bia', papeis: ADMIN },
  { chave: 'avancado', rotulo: 'Avançado', icone: '🛠️', rota: '/app/avancado', grupo: 'bia', papeis: ADMIN },
  { chave: 'canais', rotulo: 'Canais', icone: '📡', rota: '/app/canais', grupo: 'loja', papeis: ADMIN },
  { chave: 'produtos', rotulo: 'Produtos', icone: '📦', rota: '/app/produtos', grupo: 'loja' },
  { chave: 'vitrine', rotulo: 'Vitrine', icone: '🏪', rota: '/app/vitrine', grupo: 'loja', papeis: ADMIN },
  { chave: 'bio', rotulo: 'Link na bio', icone: '🔗', rota: '/app/bio', grupo: 'loja', papeis: ADMIN },
  { chave: 'desempenho', rotulo: 'Desempenho', icone: '📊', rota: '/app/desempenho', grupo: 'loja', papeis: ADMIN },
  { chave: 'pedidos', rotulo: 'Pedidos', icone: '🧾', rota: '/app/pedidos', grupo: 'vendas' },
  { chave: 'transmissao', rotulo: 'Transmissão', icone: '📣', rota: '/app/transmissao', grupo: 'vendas', papeis: ADMIN },
  { chave: 'automacoes', rotulo: 'Automações', icone: '⚡', rota: '/app/automacoes', grupo: 'vendas', papeis: ADMIN },
  // Estúdio (2026-09-29): o conteúdo da loja, do post publicado ao resultado.
  // O Design mudou para cá. Gasta crédito e publica em nome da loja: dono e admin.
  { chave: 'design', rotulo: 'Design', icone: '🎨', rota: '/app/design', grupo: 'estudio', papeis: ADMIN },
  { chave: 'estudio-estrategia', rotulo: 'Estratégia', icone: '🎯', rota: '/app/estudio/estrategia', grupo: 'estudio', papeis: ADMIN },
  { chave: 'estudio-ideias', rotulo: 'Ideias', icone: '💡', rota: '/app/estudio/ideias', grupo: 'estudio', papeis: ADMIN },
  { chave: 'estudio-referencias', rotulo: 'Referências', icone: '🔖', rota: '/app/estudio/referencias', grupo: 'estudio', papeis: ADMIN },
  { chave: 'estudio-conteudos', rotulo: 'Conteúdos', icone: '📝', rota: '/app/estudio/conteudos', grupo: 'estudio', papeis: ADMIN },
  { chave: 'estudio-publicar', rotulo: 'Publicar', icone: '📸', rota: '/app/estudio/publicar', grupo: 'estudio', papeis: ADMIN },
  { chave: 'estudio-resultados', rotulo: 'Resultados', icone: '📈', rota: '/app/estudio/resultados', grupo: 'estudio', papeis: ADMIN },
  { chave: 'estudio-anuncios', rotulo: 'Anúncios', icone: '📢', rota: '/app/estudio/anuncios', grupo: 'estudio', papeis: ADMIN, liberacao: 'anuncios' },
  { chave: 'configuracoes', rotulo: 'Configurações', icone: '⚙️', rota: '/app/configuracoes', grupo: 'conta' },
  // No legado a Equipe é uma seção das Configurações; aqui é tela, porque já é React.
  { chave: 'equipe', rotulo: 'Equipe', icone: '👥', rota: '/app/equipe', grupo: 'conta', papeis: ADMIN },
  // Assinatura e cobrança são da dona: o administrador faz tudo, menos isto.
  { chave: 'plano', rotulo: 'Plano e cobrança', icone: '🧾', rota: '/app/plano', grupo: 'conta', papeis: ['dono'] },
  { chave: 'integracoes', rotulo: 'Integrações', icone: '🔌', rota: '/app/integracoes', grupo: 'conta', papeis: ADMIN },

  // ── CRM, Kanban e Intelligence ───────────────────────────────────
  //
  // COMENTADOS DE PROPÓSITO, a pedido do Bruno (2026-09-19): "pode fazer a
  // refatoração, mas deixar comentando no codigo, não mostrar na tela por
  // enquanto". No painel antigo eles também estavam com o menu comentado.
  //
  // As TELAS EXISTEM e as rotas estão registradas (`app/router.tsx`):
  // `/app/crm` e `/app/intelligence` abrem normalmente pra quem digitar o
  // endereço. O que falta é só a porta de entrada no menu — descomentar estas
  // duas linhas basta, e nada mais precisa mudar.
  //
  // Por que foram migrados mesmo sem aparecer: eram as últimas rotas vivendo no
  // `index.js`, e deixá-las pra trás significaria manter o legado servindo HTTP
  // pra sempre. A migração também fechou um vazamento entre lojas no
  // roteamento (ver docs/refatoracao/fase-2.md, 3.16a).
  //
  // { chave: 'crm', rotulo: 'CRM', icone: '📊', rota: '/app/crm', grupo: 'vendas', papeis: ADMIN },
  // { chave: 'intelligence', rotulo: 'Intelligence', icone: '🧠', rota: '/app/intelligence', grupo: 'vendas', papeis: ADMIN },

  // A porta do modo suporte (2026-09-27): todas as lojas da plataforma, pra
  // entrar numa delas como `suporte`. Por último e num grupo próprio, pra não
  // se confundir com as telas da loja aberta.
  { chave: 'super', rotulo: 'Lojas', icone: '🛟', rota: '/app/super', grupo: 'plataforma', soSuperAdmin: true },
  { chave: 'super-design', rotulo: 'Design (plataforma)', icone: '🎛️', rota: '/app/super/design', grupo: 'plataforma', soSuperAdmin: true },
];

export const GRUPOS: Record<Tela['grupo'], string> = {
  atendimento: 'Atendimento', bia: 'A Bia', loja: 'Loja', vendas: 'Vendas', estudio: 'Estúdio', conta: 'Conta', plataforma: 'Plataforma',
};

/**
 * O suporte vê o menu do admin: o servidor deixa ele LER tudo, e esconder uma
 * tela que ele pode ler só o faria pedir print pra cliente. O que é só da dona
 * (`papeis: ['dono']`, o plano e a cobrança) continua fora. As escritas que não
 * são dele o servidor recusa com a mensagem certa — o menu não é a trava.
 */
const papelDoMenu = (papel: Papel): Papel => (papel === 'suporte' ? 'admin' : papel);

export function telasPara(papel: Papel, superAdmin = false, liberadas: ReadonlySet<NonNullable<Tela['liberacao']>> = new Set()): Tela[] {
  const quem = papelDoMenu(papel);
  return TELAS.filter((t) => (t.soSuperAdmin ? superAdmin : !t.papeis || t.papeis.includes(quem)) && (!t.liberacao || liberadas.has(t.liberacao)));
}

export const porChave = (chave: string) => TELAS.find((t) => t.chave === chave) ?? null;
